import { Box } from "@mui/system";
import { Theme } from "@mui/system";
import { FieldProps } from ".";
import {
  FieldSubtype,
  TEXT_COLOR,
  TEXT_STYLE,
  TEXT_ALIGN,
  MaterialColors,
  TextStyle,
  HeadingLevel,
  ICON_COLOR,
  IconPosition,
  TextAlign,
  AlignItems,
  IconProps,
  TextVariantProps,
  DocumentPreviewLinkRequestParams,
} from "@/core/types/types";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { useMemo } from "react";
import useInterpolateText from "@/core/hooks/useInterpolateText/useInterpolateText";
import { IconComponent } from "../IconComponent/IconComponent";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { Text as ReusableTextComponent } from "@flightright/reusable-components";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";

type TextVariantAndIconProps = TextVariantProps & IconProps;

const styles = (theme: Theme) => ({
  jumbotron: {
    "& .MuiTypography-root": {
      fontSize: "95px",
      width: "100%",
      textAlign: "center",
      lineHeight: "1",
      fontFamily: "Filson, sans-serif",
      fontWeight: 700,
      [theme.breakpoints.down("sm")]: {
        fontSize: "70px",
      },
    },
  },
});

export const TextVariant: React.FC<TextVariantAndIconProps> = ({
  color,
  text,
  variant,
  fontWeight,
  textAlign,
  iconPosition,
  isJumbotron,
  variables,
  isTextSelectable = true,
  alignItems = AlignItems.Center,
  ...iconProps
}) => {
  const classes: any = useCustomStyles(styles);
  const { interpolatedText, loading } = useInterpolateText({ text });
  const userSelect = isTextSelectable ? "auto" : "none";
  const jumbotron = isJumbotron ? classes.jumbotron : "";

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        gap: 1,
        alignItems,
        textAlign,
        justifyContent: textAlign,
      }}
    >
      {iconPosition === IconPosition.IconBefore && (
        <IconComponent {...iconProps} />
      )}
      {!loading && (
        <Box
          className={isJumbotron ? classes.jumbotron : ""}
          sx={{ fontWeight, userSelect, ...jumbotron }}
        >
          <ReusableTextComponent variant={variant} color={color}>
            {interpolatedText}
          </ReusableTextComponent>
        </Box>
      )}
      {iconPosition === IconPosition.IconAfter && (
        <IconComponent {...iconProps} />
      )}
    </Box>
  );
};

export default function DocumentPreviewLink({ field }: FieldProps) {
  const text: string = t(field.text);
  const textColor: MaterialColors | undefined = field.properties?.[TEXT_COLOR];
  const textAlign: TextAlign = field.properties?.[TEXT_ALIGN] ?? TextAlign.Left;
  const isBold: boolean = field.properties?.[TEXT_STYLE] === TextStyle.Bold;
  const isJumbotron: boolean =
    field.properties?.subtype === FieldSubtype.Jumbotron;
  const alignItems = field.properties?.alignItems;
  const iconName: string | undefined =
    field.properties?.[IconPosition.IconBefore] ??
    field.properties?.[IconPosition.IconAfter] ??
    undefined;
  const iconColor = field.properties?.[ICON_COLOR];
  const iconSize = field.properties?.iconSize;

  const iconPosition = field.properties?.[IconPosition.IconBefore]
    ? IconPosition.IconBefore
    : field.properties?.[IconPosition.IconAfter]
    ? IconPosition.IconAfter
    : undefined;

  const headingLevel = useMemo(() => {
    const subtype: FieldSubtype | undefined = field.properties?.subtype;
    switch (subtype) {
      case FieldSubtype.Heading:
        return HeadingLevel.H1;
      case FieldSubtype.Title:
        return HeadingLevel.H2;
      case FieldSubtype.Subtitle:
        return HeadingLevel.H3;
      case FieldSubtype.Paragraph:
        return HeadingLevel.Body1;
      default:
        return HeadingLevel.Body2;
    }
  }, [field]);

  const {
    properties: { requiredInputVariables },
  } = field;
  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result } = useGetPreviousAnswers(requiredInputVariables);

  const params: DocumentPreviewLinkRequestParams = {
    caseNumber: result.legalCasePublicId,
    firstName: result.customerFirstName,
    lastName: result.customerLastName,
    street: `${result.addressStreet} ${result.adressNumber}`,
    city: result.addressCity,
    postcode: result.addressPostalCode,
    docFileUuid: result.docFileUuid,
    docPreviewLink: result.docPreviewLink,
  };

  const getDocumentPreview = () => {
    window.open(params.docPreviewLink, "_blank");
  };

  return (
    <Box display="flex" alignItems="center" gap={1} position="relative">
      <a
        key={"documentPreviewLink"}
        href={"#"}
        onClick={(e) => {
          e.preventDefault();
          getDocumentPreview();
        }}
      >
        <TextVariant
          color={textColor}
          text={text}
          variant={headingLevel}
          isJumbotron={isJumbotron}
          textAlign={textAlign}
          fontWeight={isBold || isJumbotron ? 700 : 400}
          iconPosition={iconPosition}
          iconName={iconName}
          alignItems={alignItems}
          iconColor={iconColor}
          iconSize={iconSize}
        />
      </a>
    </Box>
  );
}
